import { Component, OnInit, OnDestroy } from '@angular/core';

import { Subject, takeUntil } from 'rxjs';
import { AuthService } from '../../services/auth.service';

@Component({
    selector: 'app-impersonation-banner',
    imports: [],
    template: `
    @if (isImpersonating) {
      <div class="impersonation-banner">
        <span class="banner-dot"></span>
        <span class="banner-text">Support session</span>
        @if (expiresIn) {
          <span class="banner-sep">&middot;</span>
        }
        @if (expiresIn) {
          <span class="banner-expiry">{{ expiresIn }}</span>
        }
        <button class="banner-end" (click)="endSession()">End</button>
      </div>
    }
    `,
    styles: [`
    /* Placed inside app-shell-top-banners, which pins it to the top of the shell. */
    :host { display: block; }
    .impersonation-banner {
      background: var(--solo-session-banner-bg);
      color: var(--solo-session-banner-fg);
      padding: 3px 16px;
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 11px;
      font-weight: 500;
      letter-spacing: 0.02em;
    }
    .banner-dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: var(--solo-session-banner-dot);
      flex-shrink: 0;
    }
    .banner-text { flex-shrink: 0; }
    .banner-sep { opacity: 0.5; }
    .banner-expiry {
      opacity: 0.8;
      font-size: 11px;
    }
    .banner-end {
      margin-left: auto;
      background: rgba(255,255,255,0.15);
      color: var(--solo-session-banner-fg);
      border: 1px solid rgba(255,255,255,0.2);
      padding: 1px 10px;
      border-radius: 3px;
      font-size: 10px;
      font-weight: 500;
      cursor: pointer;
      letter-spacing: 0.03em;
      text-transform: uppercase;
    }
    .banner-end:hover {
      background: rgba(255,255,255,0.25);
    }
    @media (max-width: 600px), (pointer: coarse) {
      .impersonation-banner { padding: 6px 12px; flex-wrap: wrap; }
      .banner-end { min-height: 32px; padding: 4px 10px; }
    }
  `]
})
export class ImpersonationBannerComponent implements OnInit, OnDestroy {
  isImpersonating = false;
  expiresIn: string | null = null;
  private destroy$ = new Subject<void>();
  private timerInterval: ReturnType<typeof setInterval> | null = null;

  constructor(private authService: AuthService) {}

  ngOnInit() {
    this.authService.authSession$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.checkImpersonation();
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.timerInterval) clearInterval(this.timerInterval);
  }

  private checkImpersonation() {
    const info = this.getImpersonationInfo();
    this.isImpersonating = !!info;
    if (info?.grantExpiresOn) {
      this.updateExpiry(info.grantExpiresOn);
      if (this.timerInterval) clearInterval(this.timerInterval);
      this.timerInterval = setInterval(() => this.updateExpiry(info.grantExpiresOn!), 30000);
    }
  }

  private updateExpiry(expiresOnIso: string) {
    const expiresOn = new Date(expiresOnIso.endsWith('Z') ? expiresOnIso : expiresOnIso + 'Z');
    const now = new Date();
    const diffMs = expiresOn.getTime() - now.getTime();
    if (diffMs <= 0) {
      this.expiresIn = 'Expired';
      return;
    }
    const mins = Math.floor(diffMs / 60000);
    if (mins >= 60) {
      const hrs = Math.floor(mins / 60);
      this.expiresIn = `Expires in ${hrs}h ${mins % 60}m`;
    } else {
      this.expiresIn = `Expires in ${mins}m`;
    }
  }

  private getImpersonationInfo(): { grantExpiresOn?: string } | null {
    const token = (this.authService as any).authSessionSubject?.value?.token;
    if (!token) return null;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]!));
      if (payload.IsImpersonation !== 'true') return null;
      return { grantExpiresOn: payload.ImpersonationGrantExpiresOn };
    } catch {
      return null;
    }
  }

  endSession() {
    this.authService.logout();
  }
}
